import { apiClient } from './api-client';
import type {
  ChatSession,
  ChatMessage,
  ChatStats,
  PaginatedChatSessions,
  SendMessageDto,
  AssignAgentDto,
  TransferChatDto,
  EndChatSessionDto,
  LinkToTicketDto,
  ChatSessionStatus,
} from '@/types/chat';

export interface ChatFilters {
  status?: ChatSessionStatus;
  agentId?: string;
  customerId?: string;
  page?: number;
  limit?: number;
}

/**
 * The server names the holder `assignedAgentId`; the console reads `agentId`.
 * Read once here: without it every accepted chat looked unassigned, so the
 * Active tab was empty and colleagues' chats showed up as bot-handled.
 */
export function withAgent<T extends ChatSession | null | undefined>(session: T): T {
  if (!session) return session;
  const s = session as ChatSession & { assignedAgentId?: string };
  return { ...s, agentId: s.agentId ?? s.assignedAgentId } as T;
}

export const chatService = {
  async getSessions(filters: ChatFilters = {}): Promise<PaginatedChatSessions> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.agentId) params.append('agentId', filters.agentId);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/chat/sessions?${params.toString()}`);
    return { ...response.data, data: (response.data?.data ?? []).map(withAgent) };
  },

  async getWaitingSessions(): Promise<ChatSession[]> {
    const response = await apiClient.get('/chat/sessions/waiting');
    return (response.data ?? []).map(withAgent);
  },

  async getAgentSessions(agentId: string): Promise<ChatSession[]> {
    const response = await apiClient.get(`/chat/sessions/agent/${agentId}`);
    return (response.data ?? []).map(withAgent);
  },

  async getSession(sessionId: string): Promise<ChatSession> {
    const response = await apiClient.get(`/chat/sessions/${sessionId}`);
    return withAgent(response.data);
  },

  async assignAgent(sessionId: string, data: AssignAgentDto): Promise<ChatSession> {
    const response = await apiClient.post(`/chat/sessions/${sessionId}/assign`, data);
    return withAgent(response.data);
  },

  async transferChat(
    sessionId: string,
    data: TransferChatDto,
    currentAgentId: string,
  ): Promise<ChatSession> {
    const response = await apiClient.post(
      `/chat/sessions/${sessionId}/transfer?currentAgentId=${currentAgentId}`,
      data,
    );
    return withAgent(response.data);
  },

  async endSession(sessionId: string, data: EndChatSessionDto = {}): Promise<ChatSession> {
    const response = await apiClient.post(`/chat/sessions/${sessionId}/end`, data);
    return withAgent(response.data);
  },

  async sendMessage(sessionId: string, data: SendMessageDto): Promise<ChatMessage> {
    const response = await apiClient.post(`/chat/sessions/${sessionId}/messages`, data);
    return response.data;
  },

  async getMessages(sessionId: string, limit?: number): Promise<ChatMessage[]> {
    const url = limit
      ? `/chat/sessions/${sessionId}/messages?limit=${limit}`
      : `/chat/sessions/${sessionId}/messages`;
    const response = await apiClient.get(url);
    return response.data;
  },

  async linkToTicket(sessionId: string, data: LinkToTicketDto): Promise<ChatSession> {
    const response = await apiClient.post(`/chat/sessions/${sessionId}/link-ticket`, data);
    return response.data;
  },

  async getStats(): Promise<ChatStats> {
    const response = await apiClient.get('/chat/stats');
    return response.data;
  },

  async getAgentStatus(agentId: string): Promise<{ online: boolean }> {
    const response = await apiClient.get(`/chat/agents/${agentId}/status`);
    return response.data;
  },
};

export default chatService;
